/**
 * Shared event definitions — PSV-1336 (Combat Phase 1).
 *
 * Events are declared as factory functions annotated with /** @event *\/.
 * The SDK analyzer picks up each function's name as the event topic and
 * generates sugar (e.g. `arena.send(ProjectileFired(...))`) from the
 * factory name. See API Reference → Annotations → @event.
 *
 * Phase 1 scope: client publishes `ProjectileFired` directly into the
 * arena group; every subscriber renders the projectile from those broadcast
 * fields with deterministic motion. There is no server hit detection yet,
 * so `ProjectileHit` is declared for Phase 2 readiness but has no producer
 * in Phase 1 — clients just won't see hit sparks until Phase 2 lands.
 *
 * Phase 2 will replace the client-direct `ProjectileFired` publish with a
 * `FireRequest` → server `doFire` validator → server-rebroadcasts
 * `ProjectileFired` pattern. The payload shape stays the same so render
 * code does not change between phases.
 */

/**
 * Client-to-server fire request (PSV-1336 Phase 2). Client publishes this
 * when fire input fires; server validates cooldown + looks up the
 * shooter's authoritative ship position, then broadcasts the canonical
 * `ProjectileFired`. Origin (x, y) is NOT trusted from the client — it
 * comes from server's ship map. Only `angle` (heading) and `userId`
 * (sender identity) ride on the wire.
 *
 * `userId` MUST be included because the server's hand-rolled
 * `PlayServClient.extractUserId` recovers `ctx.userId` from the inner
 * payload's `userId` field (NOT from the WS envelope — backend group
 * fan-out strips sender identity from the envelope per PSV-1334 finding).
 * Mirror of the `ShipInput` / `PlayerJoined` convention: every payload
 * that needs sender identity on the server includes it explicitly.
 *
 * @event
 */
export function FireRequest(
  userId: string,
  angle: number,
): FireRequestPayload {
  return { userId, angle };
}

/**
 * Projectile fired by a ship. Broadcast into the arena group at the moment
 * of fire. Receivers compute current position deterministically from
 * `fireTime + (x, y, angle, speed)` so all clients agree on the projectile
 * trajectory regardless of when they receive the event.
 *
 * Phase 2: published exclusively by the server in response to a validated
 * `FireRequest`. Direct client publishes still reach other subscribers via
 * group fan-out (the broadcast layer has no server-side rejection hook —
 * see PSV-1342 SDK gap), but the client-side dedup logic and HUD trust
 * the server's broadcasts.
 *
 * @event
 */
export function ProjectileFired(
  shooterId: string,
  x: number,
  y: number,
  angle: number,
  speed: number,
  dmg: number,
  fireTime: number,
): ProjectileFiredPayload {
  return { shooterId, x, y, angle, speed, dmg, fireTime };
}

/**
 * Server-detected hit. Broadcast into the arena group when a live projectile
 * intersects a ship or asteroid. Phase 1 has no producer — declared now so
 * the schema is stable when Phase 2 adds the server-side hit-detection tick.
 *
 * @event
 */
export function ProjectileHit(
  shooterId: string,
  victimType: 'ship' | 'asteroid',
  victimId: string,
  x: number,
  y: number,
): ProjectileHitPayload {
  return { shooterId, victimType, victimId, x, y };
}

/**
 * Payload shape returned by `FireRequest`. Server uses `userId` to look
 * up the authoritative ship position (origin) and to gate the per-session
 * cooldown; `angle` is the only physics input the client is trusted
 * about — origin / speed / dmg are server-controlled.
 */
export interface FireRequestPayload extends Record<string, unknown> {
  userId: string;
  angle: number;
}

/**
 * Payload shape returned by `ProjectileFired`. Consumers receive this as
 * `Record<string, unknown>` over the wire (PlayServ broadcast envelope drops
 * type info) and narrow defensively — see `handleProjectileFired` in
 * `ArenaController`. Exported for documentation; callers parsing the wire
 * payload must still validate each field.
 *
 * Field semantics:
 *   - `shooterId`: PlayServ session userId of the firing player.
 *   - `x`, `y`: world coordinates at the moment of fire (shooter's view).
 *   - `angle`: heading in radians, math convention (0 = +x, -π/2 = -y).
 *   - `speed`: pixels per second (Phase 1 fixed at 600).
 *   - `dmg`: damage points (Phase 1 unused on the client; included so the
 *     wire schema does not change when Phase 2 wires server hit detection).
 *   - `fireTime`: shooter's `Date.now()` millis at fire. Receivers use it
 *     to compute spawn position adjusted for network latency, and to
 *     drive TTL expiry.
 */
export interface ProjectileFiredPayload extends Record<string, unknown> {
  shooterId: string;
  x: number;
  y: number;
  angle: number;
  speed: number;
  dmg: number;
  fireTime: number;
}

/**
 * Payload shape returned by `ProjectileHit`. No producer in Phase 1.
 */
export interface ProjectileHitPayload extends Record<string, unknown> {
  shooterId: string;
  victimType: 'ship' | 'asteroid';
  victimId: string;
  x: number;
  y: number;
}

/**
 * Server-broadcast damage notification (PSV-1336 Phase 3). Published right
 * after `ProjectileHit` with the victim's post-damage HP. Clients use this
 * to drive HP bars, hit flashes, and so on without having to derive damage
 * from successive GameState snapshots.
 *
 * @event
 */
export function ShipDamaged(
  userId: string,
  hp: number,
  hpMax: number,
  dmg: number,
): ShipDamagedPayload {
  return { userId, hp, hpMax, dmg };
}

/**
 * Server-broadcast kill notification (PSV-1336 Phase 3). Published when a
 * `ShipDamaged` brings HP to 0. Carries the killer's userId for the kill
 * feed UI and the death position for an explosion sprite.
 *
 * @event
 */
export function ShipKilled(
  userId: string,
  killerId: string,
  x: number,
  y: number,
): ShipKilledPayload {
  return { userId, killerId, x, y };
}

/**
 * Server-broadcast respawn notification (PSV-1336 Phase 3). Published
 * `RESPAWN_DELAY_MS` after a `ShipKilled` when the server places the
 * victim back on the spawn ring with full HP. Clients use this to fade
 * the "You died" overlay (own ship) or re-show a remote sprite.
 *
 * @event
 */
export function ShipRespawned(
  userId: string,
  x: number,
  y: number,
  hp: number,
): ShipRespawnedPayload {
  return { userId, x, y, hp };
}

/**
 * Payload shape returned by `ShipDamaged`. `dmg` is the amount taken on
 * THIS hit (not cumulative); `hp` is the resulting HP after the hit.
 */
export interface ShipDamagedPayload extends Record<string, unknown> {
  userId: string;
  hp: number;
  hpMax: number;
  dmg: number;
}

/**
 * Payload shape returned by `ShipKilled`. `(x, y)` is the world position
 * where the killing blow connected — same coordinate convention as
 * `ProjectileFired` (math angle, Phaser-rendered).
 */
export interface ShipKilledPayload extends Record<string, unknown> {
  userId: string;
  killerId: string;
  x: number;
  y: number;
}

/**
 * Payload shape returned by `ShipRespawned`. `hp` echoes the post-respawn
 * HP (== `MatchConfig.ShipMaxHP` in the current single-ship-class MVP).
 */
export interface ShipRespawnedPayload extends Record<string, unknown> {
  userId: string;
  x: number;
  y: number;
  hp: number;
}

/**
 * PSV-1337 Asteroid lifecycle events — server is the authoritative source
 * for spawn/motion/hit/destroy. Clients render asteroids and HP bars from
 * these broadcasts (no client-side prediction). Drift updates are pushed
 * at 10Hz (every 6 physics ticks) to keep wire traffic bounded; visible
 * motion between updates uses local lerp (100ms slot to next push).
 */

/** @event */
export function AsteroidSpawned(
  id: string,
  x: number,
  y: number,
  radius: number,
  hpMax: number,
  hp: number,
  bonusType: string,
): AsteroidSpawnedPayload {
  return { id, x, y, radius, hpMax, hp, bonusType };
}

/** @event */
export function AsteroidUpdated(
  id: string,
  hp: number,
): AsteroidUpdatedPayload {
  return { id, hp };
}

/** @event */
export function AsteroidMoved(
  id: string,
  x: number,
  y: number,
): AsteroidMovedPayload {
  return { id, x, y };
}

/** @event */
export function AsteroidDestroyed(
  id: string,
  x: number,
  y: number,
  gemValue: number,
  bonusType: string,
  shooterId: string,
): AsteroidDestroyedPayload {
  return { id, x, y, gemValue, bonusType, shooterId };
}

/**
 * PSV-1337 — published when a bonus type is applied to a player. For
 * `heal` / `gemMult` (instant effects) `expiresAt` is 0; for buff types
 * (damageBoost / shield / speedBoost) `expiresAt` is the absolute server
 * `Date.now()` deadline. Clients use this to drive the buff icon HUD
 * countdown and the gems-multiplier visual.
 *
 * @event
 */
export function BonusApplied(
  userId: string,
  bonusType: string,
  expiresAt: number,
): BonusAppliedPayload {
  return { userId, bonusType, expiresAt };
}

/** @event */
export function BuffExpired(
  userId: string,
  bonusType: string,
): BuffExpiredPayload {
  return { userId, bonusType };
}

/**
 * PSV-1337 — published when a `shield`-buffed ship would have taken a
 * fatal/normal projectile hit but the buff absorbed it. Clients render
 * a brief expanding blue ring at the victim's position; no HP change.
 *
 * @event
 */
export function ShieldBlocked(
  userId: string,
  x: number,
  y: number,
): ShieldBlockedPayload {
  return { userId, x, y };
}

export interface AsteroidSpawnedPayload extends Record<string, unknown> {
  id: string;
  x: number;
  y: number;
  radius: number;
  hpMax: number;
  hp: number;
  /** Empty string = no bonus; otherwise one of the 5 bonus type names. */
  bonusType: string;
}

export interface AsteroidUpdatedPayload extends Record<string, unknown> {
  id: string;
  hp: number;
}

export interface AsteroidMovedPayload extends Record<string, unknown> {
  id: string;
  x: number;
  y: number;
}

export interface AsteroidDestroyedPayload extends Record<string, unknown> {
  id: string;
  x: number;
  y: number;
  gemValue: number;
  bonusType: string;
  shooterId: string;
}

export interface BonusAppliedPayload extends Record<string, unknown> {
  userId: string;
  bonusType: string;
  /** `Date.now()`-ms deadline for buff types; `0` for instant effects. */
  expiresAt: number;
}

export interface BuffExpiredPayload extends Record<string, unknown> {
  userId: string;
  bonusType: string;
}

export interface ShieldBlockedPayload extends Record<string, unknown> {
  userId: string;
  x: number;
  y: number;
}

// =========================================================================
//  PSV-1338 — ship upgrades
// =========================================================================

/**
 * Client-to-server upgrade-tier purchase request. The shooter clicks the
 * Upgrade button on the bottom-right HUD panel; the client publishes this
 * into the arena group with the sender's userId. Server reads the
 * authoritative `Player` record, validates gems + tier, mutates the
 * record, and broadcasts `UpgradeApplied` or `UpgradeRejected` back.
 *
 * `userId` rides on the wire (same convention as `FireRequest`) — the
 * server reads `ctx.userId` out of the inner payload because group fan-
 * out strips sender identity from the WS envelope (PSV-1334 finding).
 *
 * @event
 */
export function UpgradeRequest(userId: string): UpgradeRequestPayload {
  return { userId };
}

export interface UpgradeRequestPayload extends Record<string, unknown> {
  userId: string;
}

/**
 * Server confirms a successful tier purchase. Broadcast into the group
 * so other players also see `userId`'s new combat caps reflected on the
 * next GameState frame (HP bar cap, weapon damage). The client renders
 * a local toast on its own confirmation and refreshes the HUD panel.
 *
 * `gemsAfter` is included so the client can immediately reflect the
 * deduction without waiting for the `Player.listen()` push to land.
 *
 * @event
 */
export function UpgradeApplied(
  userId: string,
  newTier: number,
  gemsAfter: number,
): UpgradeAppliedPayload {
  return { userId, newTier, gemsAfter };
}

export interface UpgradeAppliedPayload extends Record<string, unknown> {
  userId: string;
  newTier: number;
  gemsAfter: number;
}

/**
 * Server rejects an upgrade request. Reasons are a closed enum so the
 * client can render a localized toast without scraping a free-text
 * message:
 *   - 'MAX_TIER'         — already at `MAX_TIER`, can't go higher.
 *   - 'NOT_ENOUGH_GEMS'  — gems balance < cost for the next tier.
 *   - 'NO_PLAYER'        — Player record missing on backend (shouldn't
 *                          happen post-spawn but covers race conditions).
 *
 * @event
 */
export function UpgradeRejected(
  userId: string,
  reason: 'MAX_TIER' | 'NOT_ENOUGH_GEMS' | 'NO_PLAYER',
): UpgradeRejectedPayload {
  return { userId, reason };
}

export interface UpgradeRejectedPayload extends Record<string, unknown> {
  userId: string;
  reason: 'MAX_TIER' | 'NOT_ENOUGH_GEMS' | 'NO_PLAYER';
}
